'use client';

import { useState, useEffect, useCallback, useRef, Fragment, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Navbar from '@/components/Navbar';
import FxDoctorGoldBanner from '@/components/FxDoctorGoldBanner';
import FeedFilterBar, { FeedFilters, FEED_TAB_DEFS } from './components/FeedFilterBar';
import TradeCard, { TradeCardData } from './components/TradeCard';
import FeedSkeleton from './components/FeedSkeleton';
import TradePostModal from './components/TradePostModal';
import FeedLeftSidebar from './components/FeedLeftSidebar';
import HowItWorksModal from './components/HowItWorksModal';
import FirstStepsCard from './components/FirstStepsCard';
import LiveEventBanner from '@/components/chat/LiveEventBanner';
import FeaturedBattleCard from './components/FeaturedBattleCard';
import WeeklyChallengeCard from './components/WeeklyChallengeCard';
import SponsoredSlot from '@/components/common/SponsoredSlot';
import { RefreshCw, TrendingUp, Loader2, Zap, BarChart2, Trophy, HelpCircle } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { XpToast } from '@/components/XpComponents';
import { useLang } from '@/lib/i18n/LanguageContext';

const FEED_LIMIT = 20;
const FEED_TAB_IDS = FEED_TAB_DEFS.map((d) => d.id);
const IDEA_TYPE_KEY = 'smt_feed_type';

// Main feed = ALL published signals & analyses, newest first (owner decision 2026-07-16).
// Personalized/curated tabs (For You, …) remain available to the right of it.
const defaultFilters: FeedFilters = {
  tab: 'all',
  ideaType: 'ALL',
  market: 'ALL',
  timeframe: 'ALL',
  direction: 'ALL',
  status: 'ALL',
  sort: 'newest',
  search: ''
};

function HomeFeedPageInner() {
  const router = useRouter();
  const { t } = useLang();
  const [filters, setFilters] = useState<FeedFilters>(defaultFilters);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [trades, setTrades] = useState<TradeCardData[]>([]);
  const [modalOpen, setModalOpen] = useState(false);

  // URL → feed state (2026-09-05 fix). ?post=true opens the composer, ?market=FOREX and ?tab=closed
  // set the filters. This used to run ONCE on mount, so the mobile drawer's market links and any
  // ?market/?tab link clicked while the feed was already open did nothing — client-side navigation
  // does not remount the page. Now it follows the search params on every change.
  const searchParams = useSearchParams();
  // Composer opened via ?post=true (TRADE+ from another page) → its "Back" button returns there.
  const openedFromOtherPageRef = useRef(false);
  useEffect(() => {
    const post = searchParams?.get('post');
    const mkt = (searchParams?.get('market') || '').toUpperCase();
    const tab = searchParams?.get('tab') as FeedFilters['tab'] | null;
    let touched = false;
    if (post === 'true') { setModalOpen(true); openedFromOtherPageRef.current = true; touched = true; }
    if (['FOREX', 'STOCKS', 'COMMODITIES', 'CRYPTO', 'INDICES'].includes(mkt)) {
      setFilters((prev) => ({ ...prev, market: mkt as any, tab: 'all' }));
      touched = true;
    }
    if (tab && FEED_TAB_IDS.includes(tab)) { setFilters((prev) => ({ ...prev, tab })); touched = true; }
    // Strip the one-shot params so a reload doesn't re-apply them (does not re-render the router).
    if (touched && typeof window !== 'undefined') window.history.replaceState({}, '', window.location.pathname);
  }, [searchParams]);
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);
  const [postedCount, setPostedCount] = useState(0); // bumps FirstStepsCard after a post from this page
  const [currentUserId, setCurrentUserId] = useState<string | undefined>(undefined);

  // TRADE+ button (mobile bottom nav) fires this when already on the feed. Keyed on currentUserId —
  // the previous mount-only listener captured `undefined` and sent signed-in users to the login page.
  useEffect(() => {
    function handleOpenPostModal() {
      if (currentUserId) setModalOpen(true);
      else window.location.href = '/sign-up-login-screen';
    }
    window.addEventListener('smt:open-post-modal', handleOpenPostModal);
    return () => window.removeEventListener('smt:open-post-modal', handleOpenPostModal);
  }, [currentUserId]);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [highlightedTradeId, setHighlightedTradeId] = useState<string | null>(null);
  const [xpToast, setXpToast] = useState<{ amount: number; reason: string; newXp?: number; newLevel?: number; prevLevel?: number } | null>(null);
  const prevLevelRef = useRef<number>(1);
  // Track seen IDs to prevent duplicates when appending pages
  const seenIdsRef = useRef<Set<string>>(new Set());

  // Listen for XP earned events
  useEffect(() => {
    function handleXpEarned(e: Event) {
      const detail = (e as CustomEvent).detail;
      if (!detail) return;
      const prevLevel = prevLevelRef.current;
      prevLevelRef.current = detail.level ?? prevLevel;
      setXpToast({
        amount: detail.amount,
        reason: detail.reason,
        newXp: detail.xp,
        newLevel: detail.level,
        prevLevel,
      });
    }
    window.addEventListener('xp-earned', handleXpEarned);
    return () => window.removeEventListener('xp-earned', handleXpEarned);
  }, []);

  // Get current user
  useEffect(() => {
    const supabase = createClient();
    supabase.auth.getUser().then(({ data: { user } }) => {
      setCurrentUserId(user?.id);
    });
  }, []);


  // Supabase real-time subscription — listen for new TradeIdea inserts
  useEffect(() => {
    const supabase = createClient();
    const channel = supabase
      .channel('home-feed-realtime')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'TradeIdea' },
        async (payload) => {
          const newId = payload.new?.id as string | undefined;
          if (!newId) return;
          // Skip if we already have this trade in the feed
          if (seenIdsRef.current.has(newId)) return;

          try {
            // Fetch the full trade card data via the existing API
            const res = await fetch(`/api/trades/${newId}`);
            if (!res.ok) return;
            const { trade } = await res.json();
            // Validate: skip if trade is missing, has no author, or has no userId
            if (!trade || !trade.id || !trade.author?.userId) return;
            if (typeof trade.entryPrice !== 'number' || typeof trade.stopLoss !== 'number' || typeof trade.takeProfit1 !== 'number') return;

            seenIdsRef.current.add(newId);
            setTrades((prev) => [trade, ...prev]);
            // Highlight the incoming trade for 3 seconds
            setHighlightedTradeId(newId);
            setTimeout(() => setHighlightedTradeId(null), 3000);
          } catch {
            // Silently ignore fetch errors in realtime handler
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchTrades = useCallback(async (currentFilters: FeedFilters, currentOffset: number, append: boolean) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setFetchError(null);
    }

    try {
      const params = new URLSearchParams();
      if (currentFilters.market !== 'ALL') params.set('market', currentFilters.market);
      if (currentFilters.timeframe !== 'ALL') params.set('timeframe', currentFilters.timeframe);
      if (currentFilters.direction !== 'ALL') params.set('direction', currentFilters.direction);
      if (currentFilters.ideaType !== 'ALL') params.set('ideaType', currentFilters.ideaType);

      // Pass tab to API for server-side filtering
      params.set('tab', currentFilters.tab);

      // Map tabs to status filters — 'closed' is handled server-side via tab param
      if (currentFilters.tab === 'open') {
        params.set('status', 'OPEN');
      } else if (currentFilters.status !== 'ALL' && currentFilters.tab !== 'closed') {
        params.set('status', currentFilters.status);
      }

      if (currentFilters.search) params.set('search', currentFilters.search);
      // Map top_rated tab to most_liked sort
      params.set('sort', currentFilters.tab === 'top_rated' ? 'most_liked' : currentFilters.sort);
      params.set('limit', String(FEED_LIMIT));
      params.set('offset', String(currentOffset));

      const res = await fetch(`/api/trades?${params.toString()}`);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to load feed');
      }
      const { trades: fetchedTrades, hasMore: more } = await res.json();
      let result: TradeCardData[] = fetchedTrades || [];

      // Filter following tab
      if (currentFilters.tab === 'following') {
        result = result.filter((t) => t.author.isFollowing);
      }

      // If no real trades exist and this is the initial load (no filters, no append),
      // No placeholder "demo" cards any more (2026-09-05): an empty feed shows the empty state.
      if (append) {
        // Deduplicate: only add trades not already in the feed
        const newTrades = result.filter((t) => !seenIdsRef.current.has(t.id));
        newTrades.forEach((t) => seenIdsRef.current.add(t.id));
        setTrades((prev) => [...prev, ...newTrades]);
      } else {
        // Fresh load: reset seen IDs
        seenIdsRef.current = new Set(result.map((t) => t.id));
        setTrades(result);
      }

      setHasMore(more ?? false);
      setOffset(currentOffset + result.length);
    } catch (err: any) {
      console.error('Feed fetch error:', err);
      if (!append) {
        setFetchError(err.message || 'Failed to load trades');
        setTrades([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // Fetch on mount and filter changes — always reset to offset 0
  useEffect(() => {
    seenIdsRef.current = new Set();
    setOffset(0);
    setHasMore(false);
    fetchTrades(filters, 0, false);
  }, [filters, fetchTrades]);

  // The Signals/Analyses choice is remembered per browser (smt_feed_type), restored after mount.
  useEffect(() => {
    try {
      const saved = localStorage.getItem(IDEA_TYPE_KEY);
      if (saved === 'SIGNAL' || saved === 'ANALYSIS') setFilters((prev) => ({ ...prev, ideaType: saved }));
    } catch { /* ignore */ }
  }, []);

  function handleFilterChange(partial: Partial<FeedFilters>) {
    setFilters((prev) => {
      const next = { ...prev, ...partial };
      // Analyses never settle (always OPEN), so lifecycle views are signals-only: picking Analyses
      // drops them, and picking a lifecycle view while on Analyses flips back to Signals.
      if (partial.ideaType === 'ANALYSIS') {
        next.status = 'ALL';
        if (next.tab === 'open' || next.tab === 'closed') next.tab = 'all';
      } else if (prev.ideaType === 'ANALYSIS' && (partial.tab === 'open' || partial.tab === 'closed' || (partial.status && partial.status !== 'ALL'))) {
        next.ideaType = 'SIGNAL';
      }
      if (next.ideaType !== prev.ideaType) {
        try { localStorage.setItem(IDEA_TYPE_KEY, next.ideaType); } catch { /* ignore */ }
      }
      return next;
    });
  }

  function handleTradePosted(newTrade: TradeCardData) {
    setPostedCount((n) => n + 1);
    seenIdsRef.current.add(newTrade.id);
    setTrades((prev) => {
      // Avoid duplicate if realtime already added it
      if (prev.some((t) => t.id === newTrade.id)) return prev;
      return [newTrade, ...prev];
    });
    // Highlight the new trade for 3 seconds
    setHighlightedTradeId(newTrade.id);
    setTimeout(() => setHighlightedTradeId(null), 3000);
  }

  function handleStatusUpdated(tradeId: string, status: any) {
    setTrades((prev) =>
    prev.map((t) => t.id === tradeId ? { ...t, status } : t)
    );
  }

  function handleLoadMore() {
    fetchTrades(filters, offset, true);
  }

  function handleRefresh() {
    seenIdsRef.current = new Set();
    setOffset(0);
    setHasMore(false);
    fetchTrades(filters, 0, false);
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-base)' }}>
      <Navbar
        onPostTrade={() => currentUserId ? setModalOpen(true) : router.push('/sign-up-login-screen')}
      />
      <FxDoctorGoldBanner />

      {/* XP Toast */}
      {xpToast && (
        <XpToast
          amount={xpToast.amount}
          reason={xpToast.reason}
          newXp={xpToast.newXp}
          newLevel={xpToast.newLevel}
          prevLevel={xpToast.prevLevel}
          onDismiss={() => setXpToast(null)}
        />
      )}

      <main style={{ maxWidth: 1280, margin: '0 auto' }} className="px-0 sm:px-4 lg:px-8 py-2 sm:py-6 pb-28 md:pb-6">
        <div className="flex gap-6">
          {/* Left Sidebar */}
          <FeedLeftSidebar
            activeTab={filters.tab}
            activeDirection={filters.direction}
            onTabChange={(tab) => handleFilterChange({ tab })}
            onDirectionFilter={(direction) => handleFilterChange({ direction: direction as any })}
          />

          {/* Main feed column — no right sidebar; capped + centered so charts scale up proportionally */}
          <div className="flex-1 min-w-0 max-w-[660px] mx-auto flex flex-col" style={{ gap: 18 }}>
            {/* Post idea prompt — desktop */}
            <div className="hidden md:block">
              <button
                onClick={() => currentUserId ? setModalOpen(true) : router.push('/sign-up-login-screen')}
                className="w-full flex items-center gap-3 p-4 rounded-2xl transition-all duration-150"
                style={{
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border)',
                  boxShadow: '0 2px 12px rgba(28,43,38,0.06)',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--accent-bright)'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 2px 20px rgba(14,124,95,0.12)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border)'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 2px 12px rgba(28,43,38,0.06)'; }}
              >
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0"
                  style={{ background: 'var(--accent)', color: 'var(--bg-base)' }}
                >
                  {currentUserId ? currentUserId[0].toUpperCase() : 'T'}
                </div>
                <div
                  className="flex-1 text-left text-sm px-3 py-2 rounded-xl"
                  style={{ background: 'var(--bg-sunken)', color: 'var(--text-muted)', border: '1px solid var(--border-subtle)' }}
                >
                  {t('Share a trade idea… (BTCUSD, EURUSD, SPY…)')}
                </div>
                <div
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold transition-all duration-150"
                  style={{ background: 'var(--accent)', color: 'var(--bg-base)' }}
                >
                  <TrendingUp size={13} />
                  {t('Post Idea')}
                </div>
              </button>
            </div>

            {/* New accounts: "first steps" checklist (hides itself once done or dismissed) */}
            {currentUserId && (
              <FirstStepsCard
                onPostTrade={() => setModalOpen(true)}
                onHowItWorks={() => setHowItWorksOpen(true)}
                refreshKey={postedCount}
              />
            )}

            {/* Next scheduled live chat session (LiveEvent) — hidden when none is coming up */}
            <LiveEventBanner variant="card" />

            {/* Engagement (2026-09-05): battle of the week with visitor voting + weekly gold challenge */}
            <FeaturedBattleCard />
            <WeeklyChallengeCard onPostTrade={() => currentUserId ? setModalOpen(true) : router.push('/sign-up-login-screen')} />

            {/* Filter bar — sits directly on the page background, no dark wrapper */}
            <FeedFilterBar filters={filters} onChange={handleFilterChange} />

            {/* Feed */}
            <div>
              {loading ? (
                <div className="p-0 sm:p-4">
                  <FeedSkeleton />
                </div>
              ) : fetchError ? (
                <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'var(--bearish-muted)' }}>
                    <TrendingUp size={26} style={{ color: 'var(--bearish)' }} />
                  </div>
                  <h3 className="text-base font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>{t('Failed to load feed')}</h3>
                  <p className="text-sm max-w-xs mb-4" style={{ color: 'var(--text-secondary)' }}>{t(fetchError)}</p>
                  <button onClick={handleRefresh} className="btn-primary px-5 py-2.5 text-sm flex items-center gap-2">
                    <RefreshCw size={14} />{t('Retry')}
                  </button>
                </div>
              ) : trades.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'var(--accent-muted)' }}>
                    <TrendingUp size={26} style={{ color: 'var(--accent)' }} />
                  </div>
                  <h3 className="text-base font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                    {filters.tab === 'following' ? t('No trades from followed traders') : filters.tab === 'for_you' ? t('No personalized trades yet') : t('No trade ideas yet')}
                  </h3>
                  <p className="text-sm max-w-xs" style={{ color: 'var(--text-secondary)' }}>
                    {filters.tab === 'following' ? t('Follow traders to see their ideas here.') : filters.tab === 'for_you' ? t('Interact with posts to personalize your feed.') : t('Be the first to post a trade idea, or adjust your filters.')}
                  </p>
                  <button onClick={() => setFilters(defaultFilters)} className="btn-primary mt-4 px-5 py-2.5 text-sm">
                    {t('Clear Filters')}
                  </button>
                </div>
              ) : (
                <div>
                  {/* Trade cards — Instagram-style vertical feed */}
                  <div className="flex flex-col gap-2 sm:gap-4 p-0 sm:p-0">
                    {trades.map((trade, i) => (
                      <Fragment key={trade.id}>
                        <TradeCard
                          trade={trade}
                          currentUserId={currentUserId}
                          onStatusUpdated={handleStatusUpdated}
                          isHighlighted={trade.id === highlightedTradeId}
                        />
                        {/* One non-aggressive sponsored slot after the 4th card (default All + For-You, ≥5 ideas) */}
                        {i === 3 && (filters.tab === 'all' || filters.tab === 'for_you') && trades.length >= 5 && (
                          <SponsoredSlot location="home_feed" />
                        )}
                      </Fragment>
                    ))}
                  </div>

                  {/* Pagination footer */}
                  <div className="flex flex-col items-center justify-center p-5 gap-3">
                    {hasMore ? (
                      <button
                        onClick={handleLoadMore}
                        disabled={loadingMore}
                        className="btn-ghost flex items-center gap-2 px-6 py-2.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loadingMore ? (
                          <><Loader2 size={14} className="animate-spin" />{t('Loading…')}</>
                        ) : (
                          <><ChevronDownIcon size={14} />{t('Load more')}</>
                        )}
                      </button>
                    ) : (
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        {t("You've reached the end of the feed")}
                      </p>
                    )}
                    <button className="btn-ghost flex items-center gap-2 px-4 py-2 text-xs" onClick={handleRefresh}>
                      <RefreshCw size={12} />{t('Refresh feed')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>
      </main>

      {/* Trade Post Modal (normal) */}
      <TradePostModal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); openedFromOtherPageRef.current = false; }}
        onTradePosted={handleTradePosted}
        onBack={() => {
          if (openedFromOtherPageRef.current && window.history.length > 1) router.back();
        }}
      />

      {/* How It Works Modal */}
      <HowItWorksModal
        isOpen={howItWorksOpen}
        onClose={() => setHowItWorksOpen(false)}
      />

      {/* Mobile Bottom Navigation */}

      <style jsx global>{`
        @keyframes heroGradientShift {
          0% { opacity: 0.6; transform: translateX(-2%) scale(1.02); }
          100% { opacity: 1; transform: translateX(2%) scale(1); }
        }
      `}</style>
    </div>
  );
}

// Inline chevron to avoid extra import
function ChevronDownIcon({ size }: {size: number;}) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>);
}

// useSearchParams needs a Suspense boundary above it (static prerender bail-out rule).
export default function HomeFeedPage() {
  return (
    <Suspense fallback={<div className="min-h-screen" style={{ background: 'var(--bg-base)' }} />}>
      <HomeFeedPageInner />
    </Suspense>
  );
}
